import { supabase } from '../../../core/lib/supabase';
import { buildExecutionTemplateDownloadFilename } from '@/lib/execution-template-download-filename';
import {
  uploadTemplateToExecutionStorage,
  uploadUserFileToExecutionTemplateStorage,
  getExecutionTemplateDisplayNameFromFile,
  BLANK_TEMPLATE_ID,
  BLANK_EXCEL_TEMPLATE_ID,
} from './uploadTemplateToExecutionStorage';

const EXECUTION_TEMPLATES_BUCKET = 'execution-templates';

const PREVIEW_EXTENSIONS = [
  '.docx',
  '.doc',
  '.xlsx',
  '.xls',
  '.pdf',
  '.html',
  '.txt',
  '.csv',
] as const;

export type TemplatePreviewSource = 'execution' | 'catalog';

/** Prefer `kind: 'blob'` in Office add-ins: avoids cross-origin fetch to signed URLs in the WebView. */
export type LatestTemplatePreviewInfo =
  | {
      kind: 'blob';
      blob: Blob;
      fileName: string;
      extension: string;
      source: TemplatePreviewSource;
    }
  | {
      kind: 'remote';
      url: string;
      fileName: string;
      extension: string;
      source: TemplatePreviewSource;
    };

export type ExecutionTemplateStatus = 'pending' | 'completed' | 'canceled';

export interface ExecutionTemplateRow {
  /** Catalog templates.id */
  id: string;
  name: string;
  type: string | null;
  status: ExecutionTemplateStatus;
  /** workpill_execution_templates.id — Storage folder + chat active template */
  execution_template_id: string;
  content_url?: string | null;
}

type JoinRow = {
  id: string;
  name: string | null;
  status: string | null;
  /** Denormalized from catalog on insert */
  type: string | null;
  templates: {
    id: string;
    name: string;
    type: string | null;
    content_url?: string | null;
  } | null;
};

export function isWordLikeTemplateType(type: string | null | undefined): boolean {
  const t = (type || '').toLowerCase();
  return t === 'word' || t === 'scratch';
}

export async function getExecutionTemplates(executionId: string): Promise<ExecutionTemplateRow[]> {
  const { data, error } = await supabase
    .from('workpill_execution_templates')
    .select('id, name, status, type, templates ( id, name, type, content_url )')
    .eq('workpill_execution_id', executionId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[executionTemplatesService] load failed:', error);
    throw new Error(error.message);
  }

  const rows = (data ?? []) as unknown as JoinRow[];
  return rows
    .map((d) => {
      type TplRow = NonNullable<JoinRow['templates']>;
      const rawTpl = d.templates as TplRow | TplRow[] | null | undefined;
      const tpl = Array.isArray(rawTpl) ? rawTpl[0] ?? null : rawTpl;
      if (!tpl) return null;
      return {
        id: tpl.id,
        name: (d.name ?? tpl.name ?? 'Unnamed').trim() || 'Unnamed',
        type: d.type ?? tpl.type ?? null,
        status: (d.status as ExecutionTemplateStatus) || 'pending',
        execution_template_id: d.id,
        content_url: tpl.content_url,
      };
    })
    .filter(Boolean) as ExecutionTemplateRow[];
}

export interface CatalogTemplate {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
}

export async function listTemplatesCatalog(search?: string): Promise<CatalogTemplate[]> {
  let q = supabase.from('templates').select('id, name, description, type').order('name');
  if (search?.trim()) {
    q = q.ilike('name', `%${search.trim()}%`);
  }
  const { data, error } = await q.range(0, 399);
  if (error) throw new Error(error.message);
  return (data || []) as CatalogTemplate[];
}

async function insertExecutionTemplateRow(
  executionId: string,
  templateId: string,
  options?: { name?: string | null }
): Promise<string> {
  const row: {
    workpill_execution_id: string;
    template_id: string;
    name?: string | null;
  } = { workpill_execution_id: executionId, template_id: templateId };
  if (options?.name != null && String(options.name).trim() !== '') {
    row.name = String(options.name).trim();
  }

  const { data, error } = await supabase.from('workpill_execution_templates').insert(row).select('id').single();

  if (error) throw new Error(error.message);
  const executionTemplateId = data?.id as string;
  if (!executionTemplateId) throw new Error('No execution template id returned');
  return executionTemplateId;
}

export async function addTemplateToExecution(
  executionId: string,
  templateId: string,
  options?: { name?: string | null }
): Promise<string> {
  const executionTemplateId = await insertExecutionTemplateRow(executionId, templateId, options);
  await uploadTemplateToExecutionStorage({ executionTemplateId, templateId });
  return executionTemplateId;
}

/** Add a new execution template from a user file (.docx / .xlsx), same flow as web `useAddExecutionTemplate`. */
export async function addUploadedFileTemplateToExecution(executionId: string, file: File): Promise<string> {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith('.docx') && !lower.endsWith('.xlsx')) {
    throw new Error('Only .docx and .xlsx files are allowed.');
  }
  const templateId = lower.endsWith('.xlsx') ? BLANK_EXCEL_TEMPLATE_ID : BLANK_TEMPLATE_ID;
  const name = getExecutionTemplateDisplayNameFromFile(file);
  const executionTemplateId = await insertExecutionTemplateRow(executionId, templateId, { name });
  await uploadUserFileToExecutionTemplateStorage({ executionTemplateId, file });
  return executionTemplateId;
}

export async function deleteExecutionTemplate(executionId: string, executionTemplateId: string): Promise<void> {
  const { data: listData, error: listError } = await supabase.storage
    .from(EXECUTION_TEMPLATES_BUCKET)
    .list(executionTemplateId);

  if (!listError && listData?.length) {
    const paths = listData.filter((f) => f.name).map((f) => `${executionTemplateId}/${f.name}`);
    if (paths.length) {
      const { error: removeError } = await supabase.storage.from(EXECUTION_TEMPLATES_BUCKET).remove(paths);
      if (removeError) {
        console.warn('[executionTemplatesService] storage remove:', removeError.message);
      }
    }
  }

  const { error } = await supabase
    .from('workpill_execution_templates')
    .delete()
    .eq('id', executionTemplateId)
    .eq('workpill_execution_id', executionId);

  if (error) throw new Error(error.message);
}

export async function updateExecutionTemplateDisplayName(
  executionTemplateId: string,
  name: string
): Promise<void> {
  const { error } = await supabase
    .from('workpill_execution_templates')
    .update({ name: name.trim() || null })
    .eq('id', executionTemplateId);

  if (error) throw new Error(error.message);
}

export async function updateExecutionTemplateFields(
  executionTemplateId: string,
  patch: { name?: string; status?: ExecutionTemplateStatus }
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    payload.name = patch.name.trim() || null;
  }
  if (patch.status !== undefined) {
    payload.status = patch.status;
  }
  if (Object.keys(payload).length === 0) return;

  const { error } = await supabase
    .from('workpill_execution_templates')
    .update(payload)
    .eq('id', executionTemplateId);

  if (error) throw new Error(error.message);
}

/**
 * Download the latest file for an execution template (same resolution as preview).
 */
export async function downloadExecutionTemplateRow(row: ExecutionTemplateRow): Promise<void> {
  const result = await getLatestTemplatePreviewInfo(row.execution_template_id, row.content_url);
  if (!result) {
    throw new Error(
      'No document available yet. Generate or upload a version from the web app, or wait for processing.'
    );
  }

  let blob: Blob;
  if (result.kind === 'blob') {
    blob = result.blob;
  } else {
    const response = await fetch(result.url);
    if (!response.ok) throw new Error(`Failed to download: ${response.statusText}`);
    blob = await response.blob();
  }

  const filename = buildExecutionTemplateDownloadFilename(row.name, result.fileName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Latest file in execution-templates/{id}/ via authenticated download, or catalog base file (`content_url`).
 */
export async function getLatestTemplatePreviewInfo(
  executionTemplateId: string,
  catalogContentUrl?: string | null
): Promise<LatestTemplatePreviewInfo | null> {
  const { data: filesData, error: listError } = await supabase.storage
    .from(EXECUTION_TEMPLATES_BUCKET)
    .list(executionTemplateId, {
      sortBy: { column: 'name', order: 'desc' },
    });

  if (!listError && filesData?.length) {
    const validFiles = filesData.filter((f) =>
      PREVIEW_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
    );
    if (validFiles.length > 0) {
      const latestFile = validFiles[0];
      const path = `${executionTemplateId}/${latestFile.name}`;
      const { data: blob, error: dlError } = await supabase.storage
        .from(EXECUTION_TEMPLATES_BUCKET)
        .download(path);
      if (!dlError && blob) {
        const extension = latestFile.name.split('.').pop()?.toLowerCase() || '';
        return {
          kind: 'blob',
          blob,
          fileName: latestFile.name,
          extension,
          source: 'execution',
        };
      }
    }
  }

  const raw = catalogContentUrl?.trim();
  if (!raw) return null;

  if (/^https?:\/\//i.test(raw)) {
    const extension = raw.split('.').pop()?.split('?')[0]?.toLowerCase() || 'docx';
    return {
      kind: 'remote',
      url: raw,
      fileName: 'template',
      extension,
      source: 'catalog',
    };
  }

  const { data: blob, error: tplDlError } = await supabase.storage.from('templates').download(raw);
  if (!tplDlError && blob) {
    const baseName = raw.split('/').pop() || 'template';
    const extension = baseName.split('.').pop()?.toLowerCase() || 'docx';
    return {
      kind: 'blob',
      blob,
      fileName: baseName,
      extension,
      source: 'catalog',
    };
  }

  const { data: signedData, error: signedError } = await supabase.storage
    .from('templates')
    .createSignedUrl(raw, 3600);

  if (!signedError && signedData?.signedUrl) {
    const baseName = raw.split('/').pop() || 'template';
    const extension = baseName.split('.').pop()?.toLowerCase() || 'docx';
    return {
      kind: 'remote',
      url: signedData.signedUrl,
      fileName: baseName,
      extension,
      source: 'catalog',
    };
  }

  return null;
}

export { BLANK_TEMPLATE_ID, BLANK_EXCEL_TEMPLATE_ID };
